import React, {useEffect} from 'react';
import {Box, Grid, Typography, Tooltip, IconButton} from '@mui/material';
import {Info} from '@mui/icons-material';
import {
  AutoFieldHelpers,
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {
  BuilderPlanForm,
  BuilderPlanFormsStyled,
} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {getSummary} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {BuilderParserStep} from '../BuilderParserStep/BuilderParserStep';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {adHocFeeFieldType} from './builderAdHocFeeFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetBuilder} from '../CreateBuilder/BuilderSourceDestination/useResetBuilder';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {PotDetails} from '../../../schema-types/PotDetails';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import {
  amountAdHocField,
  crystallisedAdHocField,
  feeTypeAdHocField,
  frequencyAdHocField,
  initialAdHocFee,
  percentAdHocField,
  reasonAdHocField,
  uncrystallisedAdHocField,
} from './BuilderAdHocFields';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {UPDATES} from '../../../shared/UPDATES';
import {loadSavedAdHocFee} from '../hooks/builderAdHocFeeHooks';
import {useAdHocFeePotsQuery} from './useAdHocFeePotsQuery';

export const BuilderAdHocFee: React.FC<BuilderProps> = ({sourceDestinationLabels}) => {
  const {sourcePots} = useAdHocFeePotsQuery();
  const {setLoaded} = useLoadedAutoField();
  const {values, errors} = useAutoForm<builderFieldsType>();
  useResetBuilder(OVRecommendActionEnum.AD_HOC_FEE);
  const {builderDetails, builderReviewId} = useBuilderContext();
  const [adhocFieldObj, adhocMeta, adhocHelpers] =
    useAutoField<builderFieldsType['adHocFee']>('adHocFee');

  useEffect(() => {
    loadSavedAdHocFee({
      builderDetails,
      adhocHelpers,
    });
    setLoaded();
  }, [builderDetails]);

  const adHocValidation = (el: PotDetails) => {
    const val = adhocFieldObj.value;
    if (val?.planId !== el.id) {
      return undefined;
    }

    const fields: boolean[] = [
      val?.feeType === 'GBP' ? !val?.monetaryFee : !val?.percentFee,
      // ON-5181 - Remove Instruction date field
      // !val?.instructionDate,
    ];

    let invalidFields = fields.filter(Boolean).length;
    if (Object.keys(errors).length) {
      invalidFields += 1;
    }

    return {
      totalFields: fields.length,
      invalidFields, // filtering out falsy values
    };
  };

  const actualChars = Number(adhocFieldObj.value?.reason?.length ?? 0);

  const sources = sourcePots.reduce<PotDetails[]>((acc, parentPot) => {
    acc.push(parentPot as unknown as PotDetails);
    if (parentPot.dentistChildren) {
      parentPot.dentistChildren.forEach((childPot) => {
        acc.push(childPot as unknown as PotDetails);
      });
    }
    return acc;
  }, []);

  const changeSource = (event: React.ChangeEvent<any> | null, value: string) => {
    const id = value || event?.target?.value;
    const pot = sources?.find((source) => source.id === id);
    const parent = sources?.find((p) => p.id === pot?.parent?.id);
    adhocHelpers.setValue(UPDATES._3352, {
      ...(values.adHocFee ?? initialAdHocFee),
      planId: id,
      planValue: Number(pot?.totalValue),
      uncrystallised: !Number(pot?.uncrystallisedFunds)
        ? Number(parent?.uncrystallisedFunds)
        : Number(pot?.uncrystallisedFunds),
      crystallised: !Number(pot?.crystallisedFunds)
        ? Number(parent?.crystallisedFunds)
        : Number(pot?.crystallisedFunds),
    });
  };

  return sourcePots?.length === 0 ? (
    <BuilderParserStep errorText="No plans available for Ad-Hoc Fees" />
  ) : (
    <BuilderColumnWrapper>
      <Grid item xs={12} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={adhocMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {sources.map((el) => (
            <BuilderPlanForm
              key={el.id}
              id={el.id}
              sameReview={el.review?.id === builderReviewId}
              reccId={el.originalOvrId}
              indent={!!el.parent?.id}
              summary={
                <Box display="flex" flexDirection="row">
                  {getSummary(el)}
                </Box>
              }
              disabled={values.mainPlanType === 'pension' && !el.parent?.id}
              details={
                <>
                  <Grid container spacing={2} sx={{mb: 2}}>
                    {values.mainPlanType === 'pension' ? (
                      <>
                        <Grid item xs={6}>
                          <Box
                            display="flex"
                            alignItems="flex-end"
                            gap={1}
                            sx={{minHeight: '56px'}}
                          >
                            <Box flex={1}>
                              <AutoField field={crystallisedAdHocField} />
                            </Box>
                            <Tooltip
                              title="Information provided here is for guidance purposes. Transaction in progress may affect the reliability of the values. This value cannot be edited in OneView."
                              placement="top"
                            >
                              <IconButton size="small" sx={{mb: 0.5, color: '#ff6b35'}}>
                                <Info fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            display="flex"
                            alignItems="flex-end"
                            gap={1}
                            sx={{minHeight: '56px'}}
                          >
                            <Box flex={1}>
                              <AutoField field={uncrystallisedAdHocField} />
                            </Box>
                            <Tooltip
                              title="Information provided here is for guidance purposes. Transaction in progress may affect the reliability of the values. This value cannot be edited in OneView."
                              placement="top"
                            >
                              <IconButton size="small" sx={{mb: 0.5, color: '#ff6b35'}}>
                                <Info fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Grid>
                      </>
                    ) : null}
                    <Grid item xs={2}>
                      <AutoField field={feeTypeAdHocField} />
                    </Grid>
                    <Grid item xs={4}>
                      {values.adHocFee?.feeType === 'GBP' ? (
                        <AutoField field={amountAdHocField} />
                      ) : (
                        <AutoField field={percentAdHocField} />
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <AutoField
                        field={{
                          ...frequencyAdHocField,
                          disabled: true,
                        }}
                      />
                    </Grid>
                    {/* ON-5181 - Remove Instruction date field */}
                    {/* <Grid item xs={3}>
                        <AutoField field={instructionDateAdHocField} helperText="" />
                        <Tooltip
                          title={
                            <>
                              <Typography mb={2} fontSize={16}>
                                The Instruction Date is the date on which the fee will be charged
                                from the selected plan.
                              </Typography>
                              <Typography mb={2} fontSize={16}>
                                {`Instruction dates are preset to the last Thursday of each month (provided it's more than 6 business days away).`}
                              </Typography>
                              <Typography fontSize={16}>
                                Ad-hoc fee will be paid out on the next day (Instruction date +1)
                              </Typography>
                            </>
                          }
                        >
                          <IconButton size="small">
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </Grid> */}
                    <Grid item xs={12}>
                      <AutoField
                        field={reasonAdHocField}
                        helperText="Up to 500 characters"
                      />
                      <Typography>
                        Character limit:{actualChars}/{Number(reasonAdHocField.charLimit)}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              }
              validation={adHocValidation(el)}
              isSelected={adhocFieldObj.value?.planId === el.id}
              isSubplan={false}
              handleChange={changeSource}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
    </BuilderColumnWrapper>
  );
};
